import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import {
  AdminContactDepartment,
  ContactDepartmentFormDialogData,
  ContactDepartmentRequestDTO
} from '@app/model/contact.model';
import { environment } from '@app/../environments/environment';
import { ContactManagementDataService } from '@app/services/contact-management-data.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { of, throwError } from 'rxjs';

interface DepartmentLanguage {
  code: string;
  label: string;
}

@Component({
  selector: 'app-contact-department-form',
  templateUrl: './contact-department-form.component.html',
  styleUrls: ['./contact-department-form.component.scss']
})
export class ContactDepartmentFormComponent implements OnInit {
  isSaving = false;
  selectedLanguageIndex = 0;

  selectedImageFile: File | null = null;
  imagePreviewUrl = '';

  languages: DepartmentLanguage[] = [
    { code: 'vi', label: 'Tiếng Việt' },
    { code: 'en', label: 'English' },
    { code: 'zh', label: '中文' }
  ];

  languageForms: { [code: string]: FormGroup } = {
    vi: this.createLanguageForm(),
    en: this.createLanguageForm(),
    zh: this.createLanguageForm()
  };

  settingsForm = new FormGroup({
    code: new FormControl('', [Validators.required]),
    icon: new FormControl(''),
    sortOrder: new FormControl(0, [Validators.required, Validators.min(0)]),
    isActive: new FormControl(true)
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ContactDepartmentFormDialogData,
    private dialogRef: MatDialogRef<ContactDepartmentFormComponent>,
    private contactManagementDataService: ContactManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.data.mode === 'edit' && this.data.department) {
      this.patchForm(this.data.department);
    }
  }

  get modalTitle(): string {
    return this.data.mode === 'edit' ? 'Sửa phòng ban' : 'Thêm phòng ban';
  }

  get submitLabel(): string {
    if (this.isSaving) {
      return 'Đang lưu...';
    }
    return this.data.mode === 'edit' ? 'Lưu thay đổi' : 'Tạo phòng ban';
  }

  closeModal(): void {
    if (this.isSaving) {
      return;
    }
    this.dialogRef.close();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) {
      return;
    }
    const file = input.files[0];
    if (file.type.indexOf('image/') !== 0) {
      this.toastr.error('Vui lòng chọn đúng định dạng hình ảnh');
      input.value = '';
      return;
    }
    this.selectedImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreviewUrl = typeof reader.result === 'string' ? reader.result : '';
    };
    reader.readAsDataURL(file);
  }

  saveDepartment(): void {
    if (this.isSaving) {
      return;
    }

    const languageEntries = this.languages.map((lang, index) => ({
      index,
      label: lang.label,
      form: this.languageForms[lang.code]
    }));

    const invalidLanguage = languageEntries.find((entry) => entry.form.invalid);

    if (invalidLanguage || this.settingsForm.invalid) {
      languageEntries.forEach((entry) => entry.form.markAllAsTouched());
      this.settingsForm.markAllAsTouched();
      if (invalidLanguage) {
        this.selectedLanguageIndex = invalidLanguage.index;
        this.toastr.error('Vui lòng nhập tên phòng ban trong tab ' + invalidLanguage.label);
        return;
      }
      this.toastr.error('Vui lòng nhập đầy đủ thiết lập phòng ban');
      return;
    }

    const vi = this.languageForms.vi.value;
    const en = this.languageForms.en.value;
    const zh = this.languageForms.zh.value;
    const settings = this.settingsForm.value;

    const payload: ContactDepartmentRequestDTO = {
      code: (settings.code || '').trim(),
      name: (vi.name || '').trim(),
      name_translations_vi: (vi.name || '').trim(),
      name_translations_en: (en.name || '').trim(),
      name_translations_zh: (zh.name || '').trim(),
      description: (vi.description || '').trim() || null,
      description_translations_vi: (vi.description || '').trim() || null,
      description_translations_en: (en.description || '').trim() || null,
      description_translations_zh: (zh.description || '').trim() || null,
      icon: (settings.icon || '').trim() || null,
      sort_order: Number(settings.sortOrder) || 0,
      is_active: settings.isActive === true
    };

    this.submitDepartment(payload);
  }

  private createLanguageForm(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [Validators.required]),
      description: new FormControl('')
    });
  }

  private patchForm(department: AdminContactDepartment): void {
    this.settingsForm.patchValue({
      code: department.code,
      icon: department.icon || '',
      sortOrder: department.sort_order,
      isActive: department.is_active
    });

    this.imagePreviewUrl = this.getImageUrl(department.image);

    this.languageForms.vi.patchValue({
      name: department.name_translations_vi || department.name || '',
      description: department.description_translations_vi || department.description || ''
    });
    this.languageForms.en.patchValue({
      name: department.name_translations_en || '',
      description: department.description_translations_en || ''
    });
    this.languageForms.zh.patchValue({
      name: department.name_translations_zh || '',
      description: department.description_translations_zh || ''
    });
  }

  private getImageUrl(imagePath: string | null): string {
    if (!imagePath) {
      return '';
    }
    if (imagePath.indexOf('http') === 0) {
      return imagePath;
    }
    return environment.backendhost + imagePath;
  }

  private submitDepartment(payload: ContactDepartmentRequestDTO): void {
    const department = this.data.department;
    const request$ =
      this.data.mode === 'edit' && department
        ? this.contactManagementDataService.updateDepartment(department.id, payload)
        : this.contactManagementDataService.createDepartment(payload);

    this.isSaving = true;

    request$
      .pipe(
        take(1),
        switchMap((response) => {
          if (!response || !response.success) {
            return of(response);
          }
          if (!this.selectedImageFile) {
            return of(response);
          }
          const departmentId =
            department && department.id
              ? department.id
              : response.data && response.data.id
                ? response.data.id
                : '';
          if (!departmentId) {
            return throwError({
              isImageUploadError: true,
              message: 'Phòng ban đã được lưu nhưng không tìm thấy ID để tải ảnh'
            });
          }
          return this.contactManagementDataService
            .uploadDepartmentImage(departmentId, this.selectedImageFile)
            .pipe(
              take(1),
              switchMap((uploadResponse) => {
                if (!uploadResponse || !uploadResponse.success) {
                  return throwError({
                    isImageUploadError: true,
                    message:
                      uploadResponse && uploadResponse.message
                        ? uploadResponse.message
                        : 'Phòng ban đã được lưu nhưng không thể tải ảnh'
                  });
                }
                return of(response);
              }),
              catchError((error) => {
                if (error && error.isImageUploadError) {
                  return throwError(error);
                }
                const errorResponse = error && error.error ? error.error : null;
                return throwError({
                  isImageUploadError: true,
                  message:
                    errorResponse && errorResponse.message
                      ? errorResponse.message
                      : 'Phòng ban đã được lưu nhưng không thể tải ảnh'
                });
              })
            );
        }),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể lưu phòng ban'
            );
            return;
          }
          const isEditing = this.data.mode === 'edit';
          this.toastr.success(
            isEditing ? 'Cập nhật phòng ban thành công' : 'Tạo phòng ban thành công'
          );
          this.dialogRef.close({ created: !isEditing, updated: isEditing });
        },
        (error) => {
          if (error && error.isImageUploadError) {
            this.toastr.error(error.message);
            return;
          }
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể lưu phòng ban';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }
          this.toastr.error(message);
        }
      );
  }
}
